import WorkerActiveManageBtn from '@/components/newPayment/WorkerPaymentInfoBox/WorkerPaymentInfoTable/WorkerPaymentInfoTableBody/WorkerActiveManageBtn';
import WorkerManageCheckBox from '@/components/newPayment/WorkerPaymentInfoBox/WorkerPaymentInfoTable/WorkerPaymentInfoTableBody/WorkerManageCheckBox';
import { useWorkerPaymentListStore } from '@/store/paymentWorkerList';
import useSubscribeStore from '@/store/subscribeMode';
import type { WorkerPaymentInfo } from '@/types/company.types';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useRef } from 'react';

interface Props {
  workerList: WorkerPaymentInfo[];
  deletedWorkerList?: WorkerPaymentInfo[];
  isApproved: 1 | 0;
  isTrial: 1 | 0;
}

const useWorkerPaymentInfoTable = ({
  workerList,
  deletedWorkerList = [],
  isApproved,
  isTrial,
}: Props) => {
  const { isSubscribeMode } = useSubscribeStore();
  const columnHelper = createColumnHelper<WorkerPaymentInfo>();

  const workerPaymentColumns = [
    columnHelper.display({
      id: 'select',
      // cell: ({ row, table }) => (
      //   <input
      //     type='checkbox'
      //     checked={row.getIsSelected()}
      //     onChange={row.getToggleSelectedHandler()}
      //     disabled={
      //       !row.getCanSelect() ||
      //       (table.getSelectedRowModel().rows.length > 0 &&
      //         (isSubscribeMode
      //           ? row.getValue('isActive') === '생성' &&
      //             row.getValue('isActive') === '비활성'
      //           : row.getValue('isActive') !==
      //             table.getSelectedRowModel().rows[0].original.isActive))
      //     }
      //   />
      // ),
      cell: ({ row }) => (
        <WorkerManageCheckBox rowData={row.original} isTrial={isTrial} />
      ),
      size: 50,
      enableResizing: false,
      enableSorting: false,
    }),
    columnHelper.accessor('name', {
      header: '아이디(이름)',
      size: 130,
    }),
    columnHelper.accessor('isActive', {
      id: 'isActive',
      header: '사용 현황',
      size: 100,
    }),
    columnHelper.display({
      header: '관리',
      id: 'actions',
      cell: ({ row }) => {
        const isActive = row.original.isActive;
        const restoreType = row.original.restoreType;
        if (isApproved === 1) {
          if (isSubscribeMode) {
            // In subscribe mode: show button for 비활성 (paid restore) and 복구 가능 (free restore)
            if (isActive === '비활성' || isActive === '복구 가능') {
              return (
                <WorkerActiveManageBtn 
                  isActive={isActive} 
                  workerId={row.id} 
                />
              );
            }
          } else {
            // Normal mode: show button for 활성 and 비활성 예정 (not restore workers)
            if ((isActive === '활성' && !restoreType) || isActive === '비활성 예정') {
              return (
                <WorkerActiveManageBtn isActive={isActive} workerId={row.id} />
              );
            }
          }
        }
      },
      size: 80,
      enableResizing: false,
      enableSorting: false,
    }),
  ];
  const { workerPaymentList, setWorkerPaymentList } =
    useWorkerPaymentListStore();

  // Memoize combined list to avoid recreating on every render
  const combinedList = useMemo(() => {
    const safeWorkerList = workerList || [];
    const safeDeletedWorkerList = deletedWorkerList || [];
    return isSubscribeMode 
      ? [...safeWorkerList, ...safeDeletedWorkerList]
      : safeWorkerList;
  }, [workerList, deletedWorkerList, isSubscribeMode]);

  // Use ref to track previous combined list to avoid unnecessary updates
  const prevCombinedListRef = useRef<WorkerPaymentInfo[]>([]);

  useEffect(() => {
    // Only update if the list actually changed (compare by JSON string for deep equality)
    const prevJson = JSON.stringify(prevCombinedListRef.current.map(w => ({ id: w.id, name: w.name, isActive: w.isActive })));
    const currentJson = JSON.stringify(combinedList.map(w => ({ id: w.id, name: w.name, isActive: w.isActive })));
    
    if (prevJson !== currentJson) {
      setWorkerPaymentList(combinedList);
      prevCombinedListRef.current = combinedList;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedList]);

  const memoizedData = useMemo(
    () => workerPaymentList ?? [],
    [workerPaymentList]
  );

  const workerPaymentReactTable = useReactTable({
    data: memoizedData,
    columns: workerPaymentColumns,

    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    sortDescFirst: true,
    enableSorting: true,
    enableMultiSort: true,
    initialState: {
      sorting: [
        {
          id: 'isActive',
          desc: true,
        },
        {
          id: 'name',
          desc: false,
        },
      ],
    },

    getRowId: (row) => String(row.id),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return { workerPaymentReactTable };
};

export default useWorkerPaymentInfoTable;
